import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchNoteById, fetchTopicById, fetchModuleById, fetchSubjectById2,
  attachQuestionToNote, detachQuestionFromNote,
} from "../../pages/services/notesService";
import { fetchQuestions } from "../../pages/services/questionService";
import { useHighlightCode } from "../utils/utility";
import "../../styles/CourseNotes.css";

const API_URL = process.env.REACT_APP_API_URL;
const renderContent = (str) => {
  if (!str) return "";
  const urlFixed = str.replace(/src="\/uploads\//g, `src="${API_URL}/uploads/`);
  if (/<[a-zA-Z]/.test(urlFixed)) return urlFixed;
  return urlFixed.replace(/\n/g, "<br>");
};

// ── Question Card ─────────────────────────────────────────────────────────────

const QuestionCard = ({ question, index, onDetach }) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [removing, setRemoving] = useState(false);
  const cardRef = useRef(null);
  useHighlightCode(cardRef, [question, showAnswer, showSolution]);

  const type = (question.questionType || "").toLowerCase();
  const isMcqSingle = type === "multiple choice single selection";
  const isMcqMulti = type === "multiple choice multi selection";
  const isCode = type === "code to compile with output";

  const correctAnswer = (question.answer || "").trim();
  const isCorrectOpt = (optText) =>
    correctAnswer.toLowerCase() === (optText || "").trim().toLowerCase();

  const diffClass = question.difficultyLevel
    ? `cn-q-badge cn-diff-${question.difficultyLevel.toLowerCase().replace(/\s+/g, "-")}`
    : "";

  const handleRemove = async () => {
    if (!window.confirm("Remove this question from the note?")) return;
    setRemoving(true);
    try {
      await onDetach(question._id);
    } catch {
      setRemoving(false);
    }
  };

  return (
    <div className="nq-card" ref={cardRef}>
      {/* Header */}
      <div className="nq-card-header">
        <div className="nq-card-meta">
          <span className="nq-num">Q{index + 1}</span>
          {question.questionType && (
            <span className="nq-type-badge">{question.questionType}</span>
          )}
          {question.difficultyLevel && (
            <span className={diffClass}>{question.difficultyLevel}</span>
          )}
        </div>
        <button
          className="nq-remove-btn"
          onClick={handleRemove}
          disabled={removing}
          title="Remove question from note"
        >
          ×
        </button>
      </div>

      {/* Question text */}
      <p className="nq-question-text" dangerouslySetInnerHTML={{ __html: renderContent(question.question) }} />

      {/* MCQ Options */}
      {(isMcqSingle || isMcqMulti) && (question.options || []).length > 0 && (
        <div className="nq-options">
          {question.options.map((opt, i) => {
            const correct = showAnswer && isCorrectOpt(opt.option);
            return (
              <div key={i} className={`nq-option${correct ? " nq-option-correct" : ""}`}>
                <span className={`nq-indicator${isMcqSingle ? " nq-radio" : " nq-checkbox"}${correct ? " nq-indicator-correct" : ""}`} />
                <span className="nq-option-text" dangerouslySetInnerHTML={{ __html: renderContent(opt.option) }} />
                {correct && <span className="nq-correct-mark">✓</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Action buttons */}
      <div className="nq-card-actions">
        <button
          className={`nq-action-btn${showAnswer ? " nq-action-btn-active" : " nq-action-btn-answer"}`}
          onClick={() => setShowAnswer((v) => !v)}
        >
          {showAnswer ? "Hide Answer" : "Show Answer"}
        </button>
        {question.answerExplanation && (
          <button
            className={`nq-action-btn${showSolution ? " nq-action-btn-active" : " nq-action-btn-solution"}`}
            onClick={() => setShowSolution((v) => !v)}
          >
            {showSolution ? "Hide Solution" : "Show Solution"}
          </button>
        )}
      </div>

      {/* Answer reveal */}
      {showAnswer && (
        <div className="nq-reveal nq-reveal-answer">
          <div className="nq-reveal-label">Answer</div>
          {isCode ? (
            <pre className="nq-code-block">{correctAnswer}</pre>
          ) : (
            <div className="nq-reveal-text" dangerouslySetInnerHTML={{ __html: renderContent(correctAnswer) }} />
          )}
        </div>
      )}

      {/* Solution reveal */}
      {showSolution && question.answerExplanation && (
        <div className="nq-reveal nq-reveal-solution">
          <div className="nq-reveal-label">Solution</div>
          <div className="nq-reveal-text" dangerouslySetInnerHTML={{ __html: renderContent(question.answerExplanation) }} />
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const NoteDetailsPage = () => {
  const { subjectId, courseId, topicId, noteId } = useParams();
  const navigate = useNavigate();

  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const notePageRef = useRef(null);
  useHighlightCode(notePageRef, [note]);

  const [subjectTitle, setSubjectTitle] = useState("");
  const [moduleTitle, setModuleTitle] = useState("");
  const [topicTitle, setTopicTitle] = useState("");

  // Question picker
  const [showPicker, setShowPicker] = useState(false);
  const [bankQs, setBankQs] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankPage, setBankPage] = useState(1);
  const [bankTotal, setBankTotal] = useState(0);
  const [bankSearch, setBankSearch] = useState("");
  const [attaching, setAttaching] = useState(null);

  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadNote = useCallback(async () => {
    try {
      setNote(await fetchNoteById(topicId, noteId));
    } catch {
      setError("Failed to load note.");
    } finally {
      setLoading(false);
    }
  }, [topicId, noteId]);

  useEffect(() => {
    loadNote();
    fetchTopicById(topicId)
      .then((t) => {
        setTopicTitle(t?.title || "");
        if (t?.moduleId)
          fetchModuleById(t.moduleId).then((m) => setModuleTitle(m?.title || "")).catch(() => {});
      })
      .catch(() => {});
    if (subjectId)
      fetchSubjectById2(subjectId).then((s) => setSubjectTitle(s?.title || "")).catch(() => {});
  }, [topicId, noteId, subjectId, loadNote]);

  const loadBank = useCallback(
    async (page) => {
      setBankLoading(true);
      try {
        const result = await fetchQuestions(page, 20);
        setBankQs(result.data || []);
        setBankTotal(result.pagination?.totalItems || 0);
      } catch {
        setBankQs([]);
      }
      setBankLoading(false);
    },
    []
  );

  useEffect(() => {
    if (showPicker) loadBank(bankPage);
  }, [showPicker, bankPage, loadBank]);

  const handleAttach = async (questionId) => {
    setAttaching(questionId);
    try {
      const result = await attachQuestionToNote(topicId, noteId, questionId);
      setNote(result.data);
      showToast("Question attached");
    } catch {
      showToast("Failed to attach question");
    }
    setAttaching(null);
  };

  const handleDetach = async (questionId) => {
    try {
      const result = await detachQuestionFromNote(topicId, noteId, questionId);
      setNote(result.data);
      showToast("Question removed");
    } catch {
      showToast("Failed to remove question");
    }
  };

  const attachedIds = new Set((note?.questions || []).map((q) => q._id?.toString()));

  const filteredBank = bankQs.filter((q) => {
    if (attachedIds.has(q._id?.toString())) return false;
    if (!bankSearch.trim()) return true;
    const s = bankSearch.toLowerCase();
    return q.question?.toLowerCase().includes(s) || q.subject?.toLowerCase().includes(s);
  });

  const bankTotalPages = Math.ceil(bankTotal / 20);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleString("en-IN", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })
      : "—";

  // Back / edit paths depending on which flow we're in
  const topicPath = subjectId
    ? `/notes/subjects/${subjectId}/topics/${topicId}`
    : courseId
    ? `/courses/${courseId}/topics/${topicId}`
    : null;

  const editPath = subjectId
    ? `/notes/subjects/${subjectId}/topics/${topicId}/notes/${noteId}/edit`
    : courseId
    ? `/courses/${courseId}/topics/${topicId}/notes/${noteId}/edit`
    : null;

  const goBack = () => (topicPath ? navigate(topicPath) : navigate(-1));

  if (loading) {
    return (
      <div className="cn-page">
        <div className="cn-loading" style={{ paddingTop: 60 }}>Loading note...</div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="cn-page">
        <div className="cn-header">
          <h2 className="cn-title">Note Details</h2>
          <button className="cn-back-btn" onClick={goBack}>← Back</button>
        </div>
        <div className="cn-error">{error || "Note not found."}</div>
      </div>
    );
  }

  const attachedQuestions = note.questions || [];

  return (
    <div className="cn-page" ref={notePageRef}>
      {toast && <div className="cn-toast">{toast}</div>}

      {/* Breadcrumb */}
      <div className="cn-breadcrumb">
        <span className="cn-breadcrumb-link" onClick={() => navigate("/notes")}>Notes</span>
        {subjectTitle && (
          <>
            <span className="cn-breadcrumb-sep"> › </span>
            <span
              className="cn-breadcrumb-link"
              onClick={() => navigate(`/notes/subjects/${subjectId}`)}
            >
              Subject: {subjectTitle}
            </span>
          </>
        )}
        {moduleTitle && (
          <>
            <span className="cn-breadcrumb-sep"> › </span>
            <span
              className="cn-breadcrumb-link"
              onClick={() => navigate(`/notes/subjects/${subjectId}`)}
            >
              Module: {moduleTitle}
            </span>
          </>
        )}
        {topicTitle && (
          <>
            <span className="cn-breadcrumb-sep"> › </span>
            <span className="cn-breadcrumb-link" onClick={goBack}>
              Topic: {topicTitle}
            </span>
          </>
        )}
        <span className="cn-breadcrumb-sep"> › </span>
        <span className="cn-breadcrumb-current">{note.title}</span>
      </div>

      {/* Header */}
      <div className="cn-header">
        <div>
          <h2 className="cn-title">{note.title}</h2>
          <p className="cn-subtitle">
            Created: {formatDate(note.createdDateTime)}
            {note.updatedDateTime !== note.createdDateTime && (
              <> &nbsp;·&nbsp; Updated: {formatDate(note.updatedDateTime)}</>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="cn-back-btn" onClick={goBack}>← Back</button>
          {editPath && (
            <button className="cn-save-btn-lg" onClick={() => navigate(editPath)}>
              Edit Note
            </button>
          )}
        </div>
      </div>

      {/* Note content */}
      <div className="cn-form-card cn-note-detail-card">
        {note.notesText ? (
          <div className="cn-note-content" dangerouslySetInnerHTML={{ __html: note.notesText }} />
        ) : (
          <p className="cn-empty">This note has no content yet.</p>
        )}
      </div>

      {/* Questions section */}
      <div className="nq-section">
        <div className="nq-section-header">
          <div>
            <h3 className="nq-section-title">
              Questions
              {attachedQuestions.length > 0 && (
                <span className="nq-count-badge">{attachedQuestions.length}</span>
              )}
            </h3>
            <p className="nq-section-hint">
              Questions attached to this note are presented as interactive exercises below.
            </p>
          </div>
          <button
            className={`cn-primary-btn${showPicker ? " nq-picker-toggle-active" : ""}`}
            onClick={() => {
              setShowPicker((v) => !v);
              setBankPage(1);
              setBankSearch("");
            }}
          >
            {showPicker ? "✕ Close" : "+ Attach Question"}
          </button>
        </div>

        {/* Question bank picker */}
        {showPicker && (
          <div className="nq-picker-panel">
            <div className="nq-picker-toolbar">
              <span className="cn-panel-title" style={{ padding: 0 }}>Question Bank</span>
              <input
                className="nq-picker-search"
                placeholder="Search by question text or subject..."
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
              />
            </div>

            {bankLoading ? (
              <div className="cn-loading">Loading...</div>
            ) : filteredBank.length === 0 ? (
              <p className="cn-empty">
                {bankSearch ? "No matching questions." : "All questions from this page are already attached."}
              </p>
            ) : (
              <div className="nq-picker-list">
                {filteredBank.map((q) => (
                  <div key={q._id} className="cn-q-row">
                    <div className="cn-q-info">
                      <span className="cn-q-text">{q.question}</span>
                      <div className="cn-q-meta">
                        {q.subject && <span className="cn-q-badge cn-q-subject">{q.subject}</span>}
                        {q.questionType && (
                          <span className="cn-q-badge nq-type-pill">{q.questionType}</span>
                        )}
                        {q.difficultyLevel && (
                          <span
                            className={`cn-q-badge cn-diff-${q.difficultyLevel.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {q.difficultyLevel}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className="cn-add-q-btn"
                      disabled={attaching === q._id}
                      onClick={() => handleAttach(q._id)}
                    >
                      {attaching === q._id ? "…" : "+ Add"}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {bankTotal > 20 && (
              <div className="cn-q-pagination">
                <button
                  disabled={bankPage <= 1}
                  onClick={() => setBankPage((p) => p - 1)}
                >
                  ← Prev
                </button>
                <span>Page {bankPage} of {bankTotalPages}</span>
                <button
                  disabled={bankPage >= bankTotalPages}
                  onClick={() => setBankPage((p) => p + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Question cards */}
        {attachedQuestions.length === 0 ? (
          <div className="nq-empty-state">
            No questions attached yet. Click "Attach Question" to add questions from the question bank.
          </div>
        ) : (
          <div className="nq-cards-list">
            {attachedQuestions.map((q, i) => (
              <QuestionCard
                key={q._id}
                question={q}
                index={i}
                onDetach={handleDetach}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NoteDetailsPage;
